import React, { useState, useEffect } from 'react'
import { useAuth } from "./api/authStore"
import moment from 'moment'
import dayjs from 'dayjs'
import useMobile from './hooks/useMobile'
import MobilePaymentsTable from './components/MobilePaymentsTable'
import { HistoryDesktopDateRangePicker } from './HistoryDateRangePickers'
import PaginationActions, { PaginationRangeText } from './components/PaginationActions'

const formatRentalSummary = (rental) =>
    [rental?.ip, rental?.country, rental?.rentalid].filter(Boolean).join(' ')

const getTransactionMemo = (row) => {
    if (row?.memo) return row.memo
    if (row?.rental) {
        if (row.type === 'rental' || row.type === 'renewal' || row.type === 'refund') {
            return formatRentalSummary(row.rental)
        }
    }
    return ''
}

const TransactionChip = ({ type }) => (
    <span className="shop-type-chip">{type}</span>
)

// Десктопная версия компонента
const DesktopPaymentsTable = (props) => {
    const { user, getAccessTokenSilently, setToken, logout } = useAuth()
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(10)
    const [paymentsList, setPaymentsList] = useState([])
    const [startDate, setStartDate] = useState(() => dayjs())
    const [endDate, setEndDate] = useState(() => dayjs().endOf('month'))
    const [activeFilter, setActiveFilter] = useState(null) // null = все, 'deposit', 'refund', 'lease'
    const [loadingButton, setLoadingButton] = useState(null) // 'first', 'prev', 'next', 'last'

    // CSS стили для толстой полосы прокрутки
    React.useEffect(() => {
        const style = document.createElement('style')
        style.textContent = `
            /* Адаптивная полоса прокрутки для таблицы PaymentsTable */
            .payments-table-container::-webkit-scrollbar {
                height: 24px; /* В 3 раза толще стандартной */
            }
            
            .payments-table-container::-webkit-scrollbar-track {
                background: var(--dark-gray);
                border-radius: 12px;
            }
            
            /* Светлая тема - оранжевая полоса */
            [data-theme="light"] .payments-table-container::-webkit-scrollbar-thumb {
                background: var(--primary-orange);
                border-radius: 12px;
                border: 3px solid var(--dark-gray);
            }
            
            [data-theme="light"] .payments-table-container::-webkit-scrollbar-thumb:hover {
                background: var(--secondary-orange);
            }
            
            /* Темная тема - светлая серая полоса */
            [data-theme="dark"] .payments-table-container::-webkit-scrollbar-thumb,
            :root:not([data-theme="light"]) .payments-table-container::-webkit-scrollbar-thumb {
                background: var(--light-gray);
                border-radius: 12px;
                border: 3px solid var(--dark-gray);
            }
            
            [data-theme="dark"] .payments-table-container::-webkit-scrollbar-thumb:hover,
            :root:not([data-theme="light"]) .payments-table-container::-webkit-scrollbar-thumb:hover {
                background: #B0B0B0;
            }
            
            /* Для Firefox - светлая тема */
            [data-theme="light"] .payments-table-container {
                scrollbar-width: thick;
                scrollbar-color: var(--primary-orange) var(--dark-gray);
            }
            
            /* Для Firefox - темная тема */
            [data-theme="dark"] .payments-table-container,
            :root:not([data-theme="light"]) .payments-table-container {
                scrollbar-width: thick;
                scrollbar-color: var(--light-gray) var(--dark-gray);
            }
        `
        document.head.appendChild(style)
        
        return () => {
            document.head.removeChild(style)
        }
    }, [])


    // Avoid a layout jump when reaching the last page with empty rows.
    const emptyRows =
        page > 0 ? Math.max(0, (1 + page) * rowsPerPage - paymentsList?.balanceRecords?.length) : 0

    const handleChangePage = React.useCallback((event, newPage, buttonType = null) => {
        const validPage = isNaN(newPage) ? 0 : Math.max(0, newPage)
        
        // Предотвращаем множественные клики
        if (loadingButton) return
        
        setLoadingButton(buttonType)
        setPage(validPage)
    }, [loadingButton])

    const handleChangeRowsPerPage = React.useCallback((event) => {
        setRowsPerPage(parseInt(event.target.value, 10))
        setPage(0)
    }, [])
    // console.log('START:'+startDate)


    useEffect(()=>{
      const callAsync = async() => {
          try {
            const dateStartFormated = startDate.format('YYYY-MM-DD')
            const dateEndFormated = endDate.format('YYYY-MM-DD')
            
            // Получаем токен один раз
            const token = await getAccessTokenSilently()
            
            const paymentsPesponse = await props.apiClient.setToken(token).getUserPayments(dateStartFormated, dateEndFormated, page, rowsPerPage)
            setPaymentsList(paymentsPesponse)
            setLoadingButton(null)
          } catch (e) {
              setLoadingButton(null)
              // console.error(e)
          }
          
      }
     
      callAsync()   
    },[startDate, endDate, page, rowsPerPage, getAccessTokenSilently])

    const handleDateRangeChange = React.useCallback((start, end) => {
      setStartDate(start)
      setEndDate(end)
    }, [])

    const getPaymentsDefaultRange = React.useCallback(() => ({
      start: dayjs().startOf('day'),
      end: dayjs().endOf('month')
    }), [])

    return (
        <div>
            <h1 className="shop-page-title" style={{ 
                marginTop: '8px',
                marginBottom: '8px',
                marginLeft: '0px',
                fontSize: 'var(--text-2xl)',
            }}>
                Transactions
            </h1>
            
            <div className="shop-filters-bar" style={{
                marginBottom: 'var(--spacing-sm)',
                display: 'flex',
                gap: 'var(--spacing-lg)',
                alignItems: 'flex-end',
                flexWrap: 'wrap',
            }}>
                <HistoryDesktopDateRangePicker
                    label="Date range"
                    startDate={startDate}
                    endDate={endDate}
                    onRangeChange={handleDateRangeChange}
                    getResetRange={getPaymentsDefaultRange}
                />
                
            </div>
            
            {paymentsList?.balanceRecords?.length > 0 ? (
                <div className="table-container payments-table-container" style={{ 
                    marginTop: 'var(--spacing-sm)',
                    overflowX: 'auto' 
                }}>
                    <table className="table table-proxies" style={{ minWidth: '600px' }}>
                        <thead>
                            <tr>
                                <th>Date</th>
                                <th>Type</th>
                                <th>Description</th>
                                <th>Amount</th>
                                <th>Balance</th>
                            </tr>
                        </thead>
                        <tbody>
                            {paymentsList?.balanceRecords?.map((row, index) => (
                                <tr key={index}>
                                    <td>{moment(row?.recordedAt).format('L LT')}</td>
                                    <td>
                                        <TransactionChip type={row?.type} />
                                    </td>
                                    <td>{getTransactionMemo(row)?.replace(/(.{80})..+/, "$1…")}</td>
                                    <td 
                                        className={row?.amount < 0 ? 'amount-negative' : 'amount-positive'}
                                        style={{ fontWeight: '600' }}
                                    >
                                        {row?.amount < 0 ? `-$${row?.amount ? (Math.abs(row.amount)/100).toFixed(2) : '0.00'}` : `+$${row?.amount ? (row.amount/100).toFixed(2) : '0.00'}`}
                                    </td>
                                    <td className="shop-accent-text" style={{ fontWeight: '600' }}>
                                        ${row?.balance ? (row.balance/100).toFixed(2) : '0.00'}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    
                    <div className="shop-table-footer" style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: 'var(--spacing-lg)',
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-md)' }}>
                            <span style={{ color: 'var(--text-muted)', fontSize: 'var(--text-sm)' }}>
                                Rows per page:
                            </span>
                            <select 
                                value={rowsPerPage}
                                onChange={handleChangeRowsPerPage}
                                style={{
                                    borderRadius: 'var(--radius-sm)',
                                    padding: '8px 12px',
                                    fontSize: 'var(--text-sm)'
                                }}
                            >
                                {[10, 20, 30, 40, 50].map(option => (
                                    <option key={option} value={option}>{option}</option>
                                ))}
                            </select>
                        </div>
                        
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-md)' }}>
                            <PaginationRangeText
                                page={page}
                                rowsPerPage={rowsPerPage}
                                count={paymentsList?.total || 0}
                            />
                            <PaginationActions 
                                page={page}
                                rowsPerPage={rowsPerPage}
                                count={paymentsList?.total || 0}
                                onPageChange={handleChangePage}
                                loadingButton={loadingButton}
                                pageAction
                            />
                        </div>
                    </div>
                </div>
            ) : (
                <div className="empty-state">
                    <div className="empty-state-icon">💰</div>
                    <h3 className="empty-state-title">No transactions found</h3>
                    <p className="empty-state-description">
                        You don't have any transaction records for the selected period.
                    </p>
                </div>
            )}
        </div>
    )
}

// Основной компонент с условным рендерингом
const PaymentsTable = (props) => {
    const { isMobile } = useMobile()


    // Если мобильная версия, используем мобильный компонент
    if (isMobile) {
        return <MobilePaymentsTable {...props} />
    }

    // Иначе используем десктопную версию
    return <DesktopPaymentsTable {...props} />
}

export default PaymentsTable